import { namedIn, shownId, withLetter } from './partsCatalogue';
import { groupOfCategory, symmetryOf, type PartItem } from './partsRun';
import type { StepBand } from './categoryBands';
import type { SubStep } from './stepsRun';
import { subBlock } from './stepHolesPrompt';
import { SUB_STEP_KINDS } from './subStepKinds';
import { GAP } from './stepAnalyzePrompt';
import { subStepLabel } from './stepsRun';

// The FULL Phase 4 hardware-table prompt, authored HERE on the client and sent verbatim — the
// contract every prompt in this app keeps, so the badge shows the string the server receives.
//
// WHICH PIECES OF HARDWARE WORK TOGETHER, over the whole manual. A screw and the barrel nut it
// threads into, a washer under that screw's head: an interaction is two hardware parts or more
// that engage one another in the finished product. What it is FOR is orientation — a 3D model
// of each is built alone, and parts that work together must be built so they meet without being
// turned — so the words ask how they engage, and along which way.
//
// ONE CALL FOR THE MANUAL and not one per step, which is the difference from the holes call: a
// washer threaded onto a screw in step 1 and the barrel nut that screw goes into in step 3 are
// one interaction, and a call about step 3 alone never sees the washer.
//
// IT READS THE ANALYSIS RATHER THAN REDOING IT, like the holes call: every gesture, its kind,
// its sentence and its parts are given, and the question is only which hardware meets which.

export type InteractionsContext = {
  steps: StepBand[];
  subSteps: Record<string, SubStep[]>;
  items: PartItem[];
  // What each assembled piece is made of, and what its maker called it — see pieceContents and
  // pieceNames. A gesture works on "103"; the answer is in supplied parts.
  contents: Map<string, string[]>;
  pieces: Map<string, { name: string }>;
};

// A kind's full words, for the one line that defines the short ones.
const kindWhat = (kind: number) => SUB_STEP_KINDS.find((k) => k.kind === kind)?.what ?? '';

// A supplied part's line: its shown id and its name.
const line = (p: PartItem, id: number) =>
  `  ${shownId(id)}: ${withLetter(namedIn(p, 'en') || `#${id}`, p.letter)}`;

// HARDWARE, AND ONLY HARDWARE. A tool is not part of the product — the Allen key turns the
// screw and leaves — so it is not in an interaction, whatever its category says.
export const isHardwarePart = (p: PartItem) => !p.tool && groupOfCategory(p.category) === 'Hardware';

// IN THE ORDER THE MODEL USES IT: the two lists, how entries are grouped (mirror pairs as the
// one exception), covers, each list's fields, then holes and faces, each said once.
const RULES = [
  'This is a furniture assembly manual, broken into steps and sub-steps below, with a drawing '
  + 'of every step. Answer two lists about its hardware (a tool is never hardware here):\n'
  + '- "interactions": every set of two or more hardware parts that engage one another in the '
  + 'assembled product\n'
  + '- "alone": every hardware part in no interaction',

  'Grouping: one entry per set of hardware joining the same component parts, listing every '
  + 'sub-step it happens in; the same set joining other parts is another entry. Except where the '
  + 'hardware meets a part marked "left and right" below: then two entries, "side": "left" and '
  + '"side": "right", each with only that side\'s sub-steps and holes. Otherwise "side": null.',

  'Covers: hardware capping another piece of hardware is in that '
  + 'fastener\'s interaction, named in "covers". It makes no hole: what it sits in is that piece\'s '
  + 'hole, so no line of its own. Hardware directly on a component part is '
  + '"alone", with "covers": true, and one "none" line.',

  'Washers: a washer is in the interaction of the hardware it goes on, named in "washers" with '
  + 'that piece — {<washer id>: <id of the hardware it goes on>}. It makes no hole: no line of its own.',

  'An interaction:\n'
  + '- hardware: its ids, in the order they come together\n'
  + '- counts: how many of each it uses, {<id>: <int>}\n'
  + '- covers: the ids that cap another one; [] when none\n'
  + '- washers: each washer and the hardware it goes on, {<washer id>: <id>}; {} when none\n'
  + '- substeps: every sub-step where the second, third... part is added to the others\n'
  + '- holes\n'
  + '- descriptionEn, descriptionFr: how they engage — which goes into, through, onto or around '
  + 'which, by which face, along which axis — one or two sentences each',

  'An "alone" entry: hardware (one id), count (how many), side, substeps (where it is added), covers (true or '
  + 'false), holes, and descriptionEn, descriptionFr (how it goes into its parts, one sentence '
  + 'each).',

  'Holes: every component part the entry\'s hardware goes into, through, onto or holds together. '
  + 'A hole is where a piece of hardware goes, and the hardware in it fills it. '
  + 'One line per kind of hole in one part: {"part": <component id>, "kind": "through" | '
  + '"blind" | "cross" | "none", "face": <face>, "count": <int>, "for": <hardware id>, '
  + '"crosses": <hardware id> (cross only), "aligned": <component id> | null (through and blind '
  + 'only), "ids": [<hole id>, ...], "portion": "leading" | "trailing" | "middle" | "whole"}. through: out the far side; blind: stops inside; '
  + 'cross: drilled across another hole of the part, whose hardware is "crosses"; '
  + 'none: met without a hole ("face": "", "count": 0). face: where the hole is entered. count: '
  + 'holes in ONE piece of that part from this entry\'s hardware. aligned: the other part the '
  + 'same hardware continues into, whose hole must line up with this one. No sizes.',

  // WHICH STRETCH OF THE HARDWARE IS IN THE HOLE — said along the axis it goes in by, so the same
  // four words do for a screw, a nut, a dowel, a cam or a cover.
  'Portion: which stretch of the line\'s hardware ("for") sits in these holes, along the axis it '
  + 'goes in by. leading: the end that goes in first; trailing: the end that goes in last, the one '
  + 'it is pushed or turned by; middle: neither end — it passes through on its way further; whole: '
  + 'all of it is in the hole. Omit it on "none".',

  // THE HOLES' IDS ARE THIS TABLE'S. Asked here, where a hole is first said to exist, and stored
  // with it; the phases after it place and drill each hole under the id it was given here.
  'Hole ids: every hole gets one id of its own, so a line has "count" of them ([] for none). '
  + 'The holes one piece of hardware runs through from one part into the next (joined by "aligned") '
  + 'are one run; a hole alone is a run of its own. Each run gets a number: "H", the number of the '
  + 'step its entry\'s first sub-step is in, and the run\'s place among that step\'s runs from 01 — '
  + 'H101, H102… for step 1, H201… for step 2 — in the order of the entries\' first sub-steps as '
  + 'listed below (an entry first at 2-1 before one first at 2-3), then, within an entry, of its '
  + 'lines. Count runs, not holes. A run of several holes letters them in the order '
  + 'the hardware goes through them: H101-A, H101-B… A cross hole takes the number of the run it '
  + 'crosses and the letter X: H101-X. A hole alone has its number and no letter. No two holes in '
  + 'the whole answer share an id.',

  'Faces: front, back, left, right, top or bottom, as in the finished product seen from its '
  + 'front — left is the viewer\'s left, top faces up.',
].join(GAP);

const TAIL = GAP + 'Reply with only the JSON:\n'
  + '{"interactions": [{"hardware": [<id>, <id>, ...], "counts": {<id>: <int>, ...}, "covers": [<id>, ...], '
  + '"washers": {<washer id>: <id>, ...}, '
  + '"side": "left" | "right" | null, '
  + '"substeps": [<"2-3">, ...], "holes": [<hole>, ...], "descriptionEn": <string>, "descriptionFr": <string>}], '
  + '"alone": [{"hardware": <id>, "count": <int>, "side": "left" | "right" | null, "substeps": [<"2-3">, ...], "covers": <bool>, '
  + '"holes": [<hole>, ...], '
  + '"descriptionEn": <string>, "descriptionFr": <string>}]}';

// The context block: the hardware, the component parts, every step's gestures labelled with
// their step, and what each assembled piece is made of. Exported so the badge shows exactly it.
export function buildInteractionsContext(ctx: InteractionsContext): string {
  const { steps, subSteps, items, contents, pieces } = ctx;
  const hardware = items.map((p, i) => [p, i] as const).filter(([p]) => isHardwarePart(p));
  const components = items.map((p, i) => [p, i] as const)
    .filter(([p]) => !p.tool && groupOfCategory(p.category) === 'Components');

  const stepBlocks = steps.flatMap((step) => {
    const subs = subSteps[step.category] ?? [];
    if (!subs.length) return [];
    return [`Step ${step.num}:\n${subs.map((sub) => subBlock(
      sub, items, pieces, subStepLabel(step.num, sub.letter), true,
    )).join('\n')}`];
  });

  // WHAT A PIECE IS MADE OF, as ids and nothing else: the lists above name every id, and the
  // sentence that works on a piece names the piece — both were spelled out again on every line,
  // eleven names deep. Only the pieces a sub-step line above points at (the rest are never
  // read), and each one's ids in order, so two lines compare at a glance.
  const named = new Set(steps.flatMap((step) => subSteps[step.category] ?? [])
    .flatMap((sub) => (sub.parts ?? []).map((u) => u.id)));
  const inside = [...contents.entries()]
    .filter(([id]) => named.has(id))
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([id, parts]) => `  ${id}: ${
      parts.filter((p) => items[Number(p)]).sort((a, b) => Number(a) - Number(b))
        .map(shownId).join(', ') || '(nothing this list knows)'}`);

  return [
    `The HARDWARE parts (id -> name) — "hardware" names only these:\n${
      hardware.map(([p, i]) => line(p, i)).join('\n') || '  (none)'}`,
    components.length
      ? `\nThe COMPONENT parts — a hole's "part" names only these:\n${
        components.map(([p, i]) => `${line(p, i)}${
          symmetryOf(p) === 'left-right' ? ' — left and right' : ''}`).join('\n')}`
      : '',
    `\nTHE SUB-STEPS, step by step — "substeps" names these labels. Each: its label, its kind, `
      + 'its sentence, and its parts as ids, brought → receiving. Kinds: position = '
      + `${kindWhat(1)}; prepare = ${kindWhat(2)}; combine = ${kindWhat(3)}. The ones tightening `
      + `a fastener already in place are left out: they add no part.\n${
      stepBlocks.join('\n\n') || '  (none analysed)'}`,
    inside.length
      ? `\nWhat each assembled piece above is MADE OF (hardware and component ids):\n${inside.join('\n')}`
      : '',
  ].filter(Boolean).join('\n');
}

export function buildInteractionsPrompt(ctx: InteractionsContext): string {
  return RULES + GAP + buildInteractionsContext(ctx) + TAIL;
}
